import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

export function useUsernameAvailability(username: string, originalUsername?: string) {
  const { checkUsernameAvailability } = useAuth();
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  const debouncedUsername = useDebouncedValue(username, 500);

  const isValidFormat = debouncedUsername.trim().length >= 3 && /^[a-zA-Z0-9]+$/.test(debouncedUsername);
  const isSameAsOriginal = originalUsername !== undefined && debouncedUsername === originalUsername;

  useEffect(() => {
    if (isSameAsOriginal) {
      setUsernameAvailable(true);
      setCheckingUsername(false);
      return;
    }

    if (!isValidFormat) {
      setUsernameAvailable(null);
      setCheckingUsername(false);
      return;
    }

    let cancelled = false;
    setCheckingUsername(true);

    (async () => {
      try {
        const result = await checkUsernameAvailability(debouncedUsername);
        if (cancelled) return;

        if (result.error) {
          setUsernameAvailable(null);
        } else {
          setUsernameAvailable(result.data?.available ?? false);
        }
      } catch {
        if (!cancelled) {
          setUsernameAvailable(null);
        }
      } finally {
        if (!cancelled) {
          setCheckingUsername(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedUsername, isValidFormat, isSameAsOriginal, checkUsernameAvailability]);

  return { usernameAvailable, checkingUsername };
}
